/**
 * DietaryStep
 *
 * Step 2 of the Generate Week wizard.
 * Allows user to select dietary preferences (vegetarian, fish).
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../../styles/theme';

export interface DietaryPreferences {
  includeVegetarian: boolean;
  includeFish: boolean;
  noPreference: boolean;
}

interface DietaryStepProps {
  preferences: DietaryPreferences;
  onTogglePreference: (key: keyof DietaryPreferences) => void;
}

interface PreferenceOption {
  key: keyof DietaryPreferences;
  icon: keyof typeof Feather.glyphMap;
  labelKey: string;
  descriptionKey: string;
}

const PREFERENCE_OPTIONS: PreferenceOption[] = [
  { key: 'includeVegetarian', icon: 'feather', labelKey: 'wizard.vegetarian', descriptionKey: 'wizard.vegetarianDesc' },
  { key: 'includeFish', icon: 'anchor', labelKey: 'wizard.fishSeafood', descriptionKey: 'wizard.fishSeafoodDesc' },
  { key: 'noPreference', icon: 'zap', labelKey: 'wizard.surpriseMe', descriptionKey: 'wizard.surpriseMeDesc' },
];

export const DietaryStep: React.FC<DietaryStepProps> = ({
  preferences,
  onTogglePreference,
}) => {
  const { t } = useTranslation();
  return (
    <View style={styles.container}>
      {/* Header */}
      <Text style={styles.header}>{t('wizard.dietaryTitle')}</Text>
      <Text style={styles.subtitle}>
        {t('wizard.dietarySubtitle')}
      </Text>

      {/* Preference Options */}
      <View style={styles.optionsContainer}>
        {PREFERENCE_OPTIONS.map((option) => {
          const isSelected = preferences[option.key];

          return (
            <TouchableOpacity
              key={option.key}
              style={[
                styles.optionCard,
                isSelected && styles.optionCardSelected,
              ]}
              onPress={() => onTogglePreference(option.key)}
              activeOpacity={0.7}
            >
              <View
                style={[
                  styles.iconContainer,
                  isSelected && styles.iconContainerSelected,
                ]}
              >
                <Feather
                  name={option.icon}
                  size={24}
                  color={isSelected ? Paprika.background : Paprika.textSecondary}
                />
              </View>

              <View style={styles.optionText}>
                <Text
                  style={[
                    styles.optionLabel,
                    isSelected && styles.optionLabelSelected,
                  ]}
                >
                  {t(option.labelKey)}
                </Text>
                <Text style={styles.optionDescription}>{t(option.descriptionKey)}</Text>
              </View>

              {/* Checkbox indicator */}
              <View
                style={[
                  styles.checkbox,
                  isSelected && styles.checkboxSelected,
                ]}
              >
                {isSelected && (
                  <Feather name="check" size={14} color={Paprika.background} />
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
    padding: PaprikaSpacing.lg,
  },

  // Header
  header: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    textAlign: 'center',
    marginBottom: PaprikaSpacing.xs,
  },
  subtitle: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    textAlign: 'center',
    marginBottom: PaprikaSpacing.xl,
  },

  // Options Container
  optionsContainer: {
    gap: PaprikaSpacing.md,
  },

  // Option Card
  optionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    borderColor: Paprika.border,
    backgroundColor: Paprika.background,
    gap: PaprikaSpacing.md,
  },
  optionCardSelected: {
    borderColor: Paprika.textPrimary,
    backgroundColor: Paprika.surfaceLight,
  },

  // Icon Container
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: Paprika.surfaceLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainerSelected: {
    backgroundColor: Paprika.textPrimary,
  },

  // Option Text
  optionText: {
    flex: 1,
    gap: 2,
  },
  optionLabel: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
  },
  optionLabelSelected: {
    fontWeight: '600',
  },
  optionDescription: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },

  // Checkbox
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: Paprika.border,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxSelected: {
    backgroundColor: Paprika.textPrimary,
    borderColor: Paprika.textPrimary,
  },
});

export default DietaryStep;
